/**
 * Doters points-redemption survey metadata (MSI-1423).
 *
 * Publishes loyalty-account and operation metadata to `window.dataLayer` so a GTM tag can
 * forward it to Hotjar as User Attributes, making it available as filterable columns in the
 * NPS survey report. The funnel deliberately never calls the Hotjar SDK: Hotjar is loaded by
 * GTM behind Cookiebot consent, so `window.hj` may not exist yet at purchase-complete time.
 *
 * Pairs with the `usedPoints=true` URL flag that triggers the survey — both are emitted under
 * the same redemption condition so attributes and trigger cannot drift apart.
 */

import errorMonitoring from '../errorMonitoring';

/** dataLayer event name the GTM tag listens for. Changing it breaks that tag. */
export const DOTERS_SURVEY_EVENT = 'doters_points_redemption';

/**
 * Resolve the Doters loyalty identity from Redux state.
 *
 * `reserhubAccount.connectedAccounts` is the authoritative source — it is the only one whose
 * shape guarantees `memberId`, and it is what the account menu reads. `siemprePlus.user` is a
 * fallback: it also gets populated by the legacy widget event, which forwards whatever the
 * widget emits and in practice carries `level` but no `memberId`.
 *
 * @param {object} state - Redux state.
 * @returns {{ memberId: (string|undefined), level: (string|undefined) }} Loyalty identity.
 */
export function selectDotersIdentity(state) {
  const dotersAccount = state?.reserhubAccount
    ?.get('connectedAccounts')
    ?.find((account) => account.provider === 'doters');
  const legacyUser = state?.siemprePlus?.get('user')?.toJS?.() ?? null;

  return {
    memberId: dotersAccount?.profile?.memberId || legacyUser?.memberId,
    level: dotersAccount?.profile?.level || legacyUser?.level,
  };
}

/**
 * Drop keys with no meaningful value so the survey report shows blanks instead of "null".
 *
 * @param {object} payload - Candidate metadata keys.
 * @returns {object} Payload without `null`, `undefined` or empty-string values.
 */
function omitEmptyValues(payload) {
  return Object.entries(payload).reduce((accumulator, [key, value]) => {
    if (value === null || value === undefined || value === '') {
      return accumulator;
    }
    return { ...accumulator, [key]: value };
  }, {});
}

/**
 * Build the flat metadata payload consumed by the GTM tag.
 *
 * Keys are `snake_case` and loyalty fields carry a `doters_` prefix: Hotjar User Attributes
 * are a per-site global namespace shared with any other `identify` call, so prefixing avoids
 * collisions. Contact data (email, phone, passenger names) is intentionally excluded.
 *
 * @param {object} params - Metadata sources already mapped by the container.
 * @param {string} [params.memberId] - Doters member identifier.
 * @param {string} [params.level] - Doters loyalty tier.
 * @param {string} [params.operationNumber] - Carrier ticket number ("# operación" on the ticket).
 * @param {string} [params.token] - Purchase token.
 * @param {string} [params.brand] - Whitelabel brand key (`env.brand`).
 * @param {number} [params.pointsUsed] - Loyalty points redeemed.
 * @param {number} [params.pointsAmount] - Monetary equivalent of the redeemed points.
 * @param {string} [params.currency] - ISO code the amount is denominated in (`env.currency`).
 * @param {string} [params.fareClass] - Service tier of the departing trip (e.g. `lujo`).
 * @returns {object} Flat payload with empty values omitted.
 */
export function buildDotersSurveyMetadata({
  memberId,
  level,
  operationNumber,
  token,
  brand,
  pointsUsed,
  pointsAmount,
  currency,
  fareClass,
} = {}) {
  return omitEmptyValues({
    doters_member_id: memberId,
    doters_level: level,
    operation_number: operationNumber,
    purchase_token: token,
    brand,
    points_used: pointsUsed,
    // Currency-neutral key plus an explicit `currency`: the funnel also serves BRL and PEN
    // tenants, so baking a currency into the key name would mislabel those amounts.
    points_amount: pointsAmount,
    currency,
    fare_class: fareClass,
  });
}

/**
 * Publish the metadata event to `window.dataLayer`.
 *
 * Failures are reported and swallowed: analytics must never break the purchase confirmation
 * screen, which is the last step of a completed payment.
 *
 * @param {object} params - Same metadata sources as {@link buildDotersSurveyMetadata}.
 * @returns {boolean} True when the event was pushed.
 */
export default function pushDotersSurveyMetadata(params) {
  try {
    const metadata = buildDotersSurveyMetadata(params);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: DOTERS_SURVEY_EVENT, ...metadata });
    return true;
  } catch (error) {
    errorMonitoring.notify({
      error,
      info: { context: 'pushDotersSurveyMetadata', token: params?.token },
    });
    return false;
  }
}
